export interface SilenceTrimResult {
  leadingSilenceFrames: number;
  trailingSilenceFrames: number;
  durationInFrames: number;
}

export async function measureTrimmedAudioFrames(src: string, fps: number): Promise<SilenceTrimResult | null> {
  if (!src || typeof window === 'undefined') return null;

  try {
    const response = await fetch(src);
    if (!response.ok) return null;

    const arrayBuffer = await response.arrayBuffer();
    const AudioContextCtor = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextCtor) return null;

    const audioContext = new AudioContextCtor();

    try {
      const decoded = await audioContext.decodeAudioData(arrayBuffer.slice(0));
      const threshold = 0.003;
      
      let firstAudibleSample = -1;
      let lastAudibleSample = -1;

      // Scan for leading silence
      for (let s = 0; s < decoded.length; s++) {
        let audible = false;
        for (let c = 0; c < decoded.numberOfChannels; c++) {
          if (Math.abs(decoded.getChannelData(c)?.[s] || 0) > threshold) {
            audible = true;
            break;
          }
        }
        if (audible) {
          firstAudibleSample = s;
          break;
        }
      }

      if (firstAudibleSample === -1) {
        // Entirely silent
        return {
          leadingSilenceFrames: 0,
          trailingSilenceFrames: Math.max(1, Math.round(decoded.duration * fps)),
          durationInFrames: 0
        };
      }

      // Scan for trailing silence
      for (let s = decoded.length - 1; s >= firstAudibleSample; s--) {
        let audible = false;
        for (let c = 0; c < decoded.numberOfChannels; c++) {
          if (Math.abs(decoded.getChannelData(c)?.[s] || 0) > threshold) {
            audible = true;
            break;
          }
        }
        if (audible) {
          lastAudibleSample = s;
          break;
        }
      }

      const leadingSeconds = firstAudibleSample / decoded.sampleRate;
      const audibleSeconds = (lastAudibleSample - firstAudibleSample + 1) / decoded.sampleRate;
      const totalSeconds = decoded.duration;
      const trailingSeconds = totalSeconds - (lastAudibleSample / decoded.sampleRate);

      return {
        leadingSilenceFrames: Math.round(leadingSeconds * fps),
        durationInFrames: Math.max(1, Math.round(audibleSeconds * fps)),
        trailingSilenceFrames: Math.round(trailingSeconds * fps)
      };
    } finally {
      await audioContext.close().catch(() => undefined);
    }
  } catch (error) {
    console.error('[trim-silence] Error measuring audio:', error);
    return null;
  }
}

type AudioLikeItem = {
  id: string;
  type: string;
  label?: string;
  src?: string;
  durationInFrames: number;
  trimStart?: number;
};

export async function collectVoiceoverTrimUpdates(
  items: AudioLikeItem[],
  fps: number
): Promise<Array<{ id: string; durationInFrames: number; leadingSilenceFrames: number }>> {
  const voiceovers = items.filter(
    (item) =>
      item.type === 'audio' &&
      item.label?.trim().toLowerCase() === 'voiceover' &&
      typeof item.src === 'string' &&
      item.src.length > 0
  );

  const measured = await Promise.all(
    voiceovers.map(async (item) => {
      const result = await measureTrimmedAudioFrames(item.src!, fps);
      if (!result) return null;
      
      const targetDuration = result.durationInFrames;
      const targetTrimStart = result.leadingSilenceFrames;
      
      // 🚀 IDEMPOTENCY CHECK: Only return if the target values differ from current ones
      const currentDuration = Math.round(item.durationInFrames);
      const currentTrimStart = Math.round(item.trimStart || 0);

      const isChanged = 
        Math.abs(targetTrimStart - currentTrimStart) > 0.5 || 
        Math.abs(targetDuration - currentDuration) > 0.5;

      if (!isChanged) return null;
      
      return { 
        id: item.id, 
        durationInFrames: targetDuration,
        leadingSilenceFrames: targetTrimStart
      };
    })
  );

  return measured.filter(
    (entry): entry is { id: string; durationInFrames: number; leadingSilenceFrames: number } => Boolean(entry)
  );
}
